import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Shield, AlertTriangle } from "lucide-react";
import client, { formatErr } from "../lib/api";
import { toast } from "sonner";

export default function StreakTokenBanner({ tokens, atRisk, streak, onChange }) {
    const [loading, setLoading] = useState(false);

    if (!atRisk && tokens === 0) return null;

    const useToken = async () => {
        setLoading(true);
        try {
            await client.post("/streak-token/use");
            toast.success("Streak shielded. Log a daily today to keep it alive.");
            if (onChange) onChange();
        } catch (e) {
            toast.error(formatErr(e));
        } finally {
            setLoading(false);
        }
    };

    if (atRisk && tokens === 0) {
        return (
            <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                data-testid="streak-at-risk-no-token"
                className="mt-4 p-3 border-l-2 border-l-neon-orange surface-1 flex items-center gap-3"
            >
                <AlertTriangle size={16} className="text-neon-orange shrink-0" />
                <div className="flex-1">
                    <div className="label-cap text-[10px] text-neon-orange">Streak At Risk</div>
                    <div className="text-xs text-zinc-300">
                        {streak}-day streak breaks if you skip today. No tokens — just log a Daily.
                    </div>
                </div>
            </motion.div>
        );
    }

    if (atRisk && tokens > 0) {
        return (
            <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                data-testid="streak-at-risk-banner"
                className="mt-4 p-4 border-2 border-neon-orange relative overflow-hidden"
                style={{
                    background: "linear-gradient(135deg, #1a0606 0%, #0d0d0d 70%)",
                    boxShadow: "0 0 24px rgba(255,59,48,0.3)",
                }}
            >
                <div className="flex items-start gap-3 relative">
                    <div className="w-10 h-10 bg-neon-orange/20 border border-neon-orange flex items-center justify-center shrink-0">
                        <AlertTriangle size={18} className="text-neon-orange" />
                    </div>
                    <div className="flex-1">
                        <div className="label-cap text-[10px] text-neon-orange">Streak At Risk</div>
                        <div className="font-display font-black text-base uppercase leading-tight">
                            {streak}-day streak about to break
                        </div>
                        <div className="text-xs text-zinc-400 mt-1">
                            Spend 1 token to shield. {tokens} available.
                        </div>
                    </div>
                </div>
                <button
                    onClick={useToken}
                    disabled={loading}
                    data-testid="streak-token-use"
                    className="btn-primary w-full py-2 mt-3 text-sm flex items-center justify-center gap-2 disabled:opacity-50"
                >
                    <Shield size={14} /> {loading ? "Shielding..." : "Use Streak Token"}
                </button>
            </motion.div>
        );
    }

    if (tokens > 0) {
        return (
            <div
                data-testid="streak-tokens-tile"
                className="mt-4 surface-1 p-3 flex items-center gap-3 border-l-2 border-l-neon-green"
            >
                <Shield size={16} className="text-neon-green" />
                <div className="flex-1">
                    <div className="label-cap text-[10px] text-zinc-500">Streak Protection</div>
                    <div className="font-display font-bold text-sm">
                        <span className="text-neon-green">{tokens}</span> token{tokens > 1 ? "s" : ""} ready
                    </div>
                </div>
                <span className="text-[10px] text-zinc-600 label-cap">Auto-redeemable</span>
            </div>
        );
    }

    return null;
}
